/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {Object<ComponentName,VariantOptions>} ComponentOptions
 */
/**
 * @typedef {Object<VariantName,Object>} VariantOptions
 */
/**
 * @typedef {string} ComponentName
 */
/**
 * @typedef {string} VariantName
 */
import ListsVariants from '@components/01.base/02.lists/variants'
import CarouselVariants from '@components/01.base/carousel/variants'
import ModalVariants from '@components/01.base/modal/variants'
import VgsFormVariants from '@components/05.security/vgs-form/variants'
import LazyloadVariants from '@directives/lazyload/variants'
/**
 * @module Store:Variants
 * @description
 * Let's you set different set of options for any given component that can be accessed with a default fallback from anywhere in the code.
 * 
 * **To create variants for a new component:**
 * - Create a folder called variants inside de component folder
 * - Create a *default.js* and *index.js* files inside the folder based on the example
 * - Import the new component variant in the store module
 * 
 * **To create new variants for a component:**
 * - Create a file *[variant-name].js* the component folder based on the *default.js* fallback options
 * - Import the new variant in the *index.js* of this component variants
 * @example
 * // default.js
 * module.exports = {
 *   name: 'default', // Should be called 'default' for the default variant options
 *   theAnswer: 42,
 *   theQuestion: null
 * }
 * // mcu.js
 * module.exports = {
 *   name: 'mcu', // This property is required in any variant
 *   theAnswer: null,
 *   theQuestion: 'What will become of phase four?'
 * }
 * // index.js
 * const DefaultVariant = require('./default')
 * const McuVariant = require('./mcu')
 * const universe = {
 *   [DefaultVariant.name]: DefaultVariant,
 *   [McuVariant.name]: McuVariant,
 * }
 * module.exports = {
 *   universe // This component name
 * }
 * // store/variant.js
 * import UniverseVariants from '@components/universe/variants'
 * ...
 * state: {
 *   ...UniverseVariants
 * }
 * // on a component
 * import VariantMixin from '@mixins/variant-mixin'
 * ...
 * mixins: [VariantMixin(
 *   // Component name
 *   'universe',
 *   // Automatic generated computed properties
 *   [ 'theAnswer', 'theQuestion' ]
 * )]
 * mounted() {
 *   console.log('the answer for the question', this.theQuestion, 'is ', this.theAnswer)
 * }
 * // on a directive
 * export const exampleDirective = {
 *   name: 'example',
 *   inserted: (el, binding, vnode) => {
 *     const store = vnode.context.$store
 *     const getProperty = store.getters['variants/getVariantSettings']
 *     const theQuestion = getProperty('universe', variant, 'theQuestion')
 *     const theAnswer = getProperty('universe', variant, 'theAnswer')
 *     console.log('the answer for the question', theQuestion, 'is ', theAnswer)
 *   },
 * }
 */
/**
 * Check if a property in the selected component / variant exists and if not returns the default value for this component
 * @param {State} state
 * @param {string} component - Name of the component to get the variant options
 * @param {string} variant - Name of the variant
 * @param {string} property - Property of the variant option
 * @returns {any}
 */
const checkExists = (state, component, variant, property) => {
  if (!state[component] || !state[component]['default']) {
    return null
  } else {
    const defaultProperty = state[component]['default'][property]
    if (!state[component][variant] || !state[component][variant][property]) {
      return defaultProperty
    } else {
      return state[component][variant][property]
    }
  }
}

export default {
  namespaced: true,
  /** Global State
   * @typedef {ComponentOptions} State
   */
  /** @type {State} */
  state: {
    ...CarouselVariants,
    ...ListsVariants,
    ...ModalVariants,
    ...VgsFormVariants,
    ...LazyloadVariants,
  },
  getters: {
    getVariantSettings: (state) => (component, variant, property) => checkExists(state, component, variant, property)
  }
}